import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, Select, Avatar } from 'antd';
import { SearchOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const Staff: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [merchantRoles, setMerchantRoles] = useState<string[]>([]);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchStaff();
    fetchEnums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search]);

  const fetchEnums = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_ENUMS);
      if (response.data?.data) {
        setMerchantRoles(response.data.data.merchantRoles || []);
      }
    } catch (error) {
      console.error('Failed to fetch enums:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_STAFF, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setStaff(data.itemsList || data.items || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || paginator.totalCount || paginator.total || 0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: (data.itemsList || data.items || []).length,
          }));
        }
      }
    } catch (error: any) {
      message.error('Failed to fetch staff');
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingStaff(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingStaff) {
        // TODO: Implement staff update endpoint if needed
        message.warning('Staff update functionality not yet implemented');
        return;
      }

      // Create new staff member
      await apiClient.post('/api/Merchant/lobby/auth/account', {
        email: values.email,
        role: values.role || undefined,
      });

      message.success('Staff created successfully. Verification email has been sent.');
      setModalVisible(false);
      form.resetFields();
      fetchStaff();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.details || 'Failed to create staff';
      message.error(errorMessage);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => (
        <Space>
          <Avatar
            src={record.portraitUrl ? `data:image/jpeg;base64,${record.portraitUrl}` : undefined}
            icon={<UserOutlined />}
            size="small"
          />
          <span>{`${record.firstName || ''} ${record.lastName || ''}`.trim() || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Country',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (code: string) => code ? (
        <Space>
          <ReactCountryFlag countryCode={code} svg style={{ width: '1.5em', height: '1.5em' }} />
          {code}
        </Space>
      ) : '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role ? <Tag color="blue">{role}</Tag> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            onClick={() => navigate(`/igwena/staff/${record.id}`)}
            style={{ color: COLORS.accent, padding: 0 }}
          >
            View Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Staff Management
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Staff
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Search</span>
              <Input
                placeholder="Search by name or email"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => {
                  const newParams = new URLSearchParams(searchParams);
                  if (search) {
                    newParams.set('search', search);
                  } else {
                    newParams.delete('search');
                  }
                  setSearchParams(newParams);
                }}
                style={{ maxWidth: 300 }}
              />
            </div>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={staff}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} staff members</span>,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title={editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingStaff ? 'Update' : 'Create'}
        width={600}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="staff@example.com" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
          >
            <Select placeholder="Select role">
              {merchantRoles.map((role) => (
                <Select.Option key={role} value={role}>
                  {role.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ marginTop: 16, padding: 12, background: COLORS.bgPrimary, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
            <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              Note: The staff member will set their first name, last name, phone number, and password when they complete registration via the invitation email.
            </Text>
          </div>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default Staff;

